import FormFieldEditInline from "@/components/form/fields/FormFieldEditInline"
import Form from "@/components/form/Form"
import FormBuilder from "@/components/form/FormBuilder"
import FormField from "@/components/form/FormField"
import HashSidebarItem, { HashSidebarItemType } from "@/components/navigation/HashSidebar/HashSidebarItem"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table"
import { toast } from "@/lib/toast"
import { getUniqueName } from "@/lib/utils"
import PanelButton from "@/pages/Scenarios/components/PanelButton"
import { CredentialType } from "@/stores/credential.store"
import { store } from "@/stores/stores"
import dayjs from "dayjs"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { Link } from "react-router"
import { useSnapshot } from "valtio"

interface CredentialFormProps {
  currentTab: string
  id: string
  type: string
  credential?: CredentialType
  onSuccess?: (response: any) => void
}

export function CredentialForm(props: CredentialFormProps) {
  const [submitting, setSubmitting] = useState(false)
  const [testing, setTesting] = useState(false)
  const snap = useSnapshot(store)
  const credentialMeta = store.credential.credentialsMeta.record[props.type]

  const tabs = [
    {
      label: "Connection",
      to: "connection/" + props.type,
    },
    {
      label: "Sharing",
      to: "sharing/" + props.type,
    },
    ...(props.id === "create"
      ? []
      : [
          {
            label: "Details",
            to: "details/" + props.type,
          },
        ]),
  ] satisfies HashSidebarItemType[]

  const form = useForm({
    defaultValues: {
      visibility: {
        type: "private",
      },
      name: getUniqueName(
        credentialMeta?.label?.split(" ")?.[0] + " account",
        snap.credential.list.map((x) => x.name),
      ),
      type: credentialMeta?.type,
      ...props.credential,
    },
  })

  return (
    <Form
      form={form}
      onSubmit={(model) => {
        setSubmitting(true)
        try {
          if (!props.credential?.id) {
            store.credential.actions
              .create(model)
              .then((response) => {
                toast.success("Credential created")
                props.onSuccess?.(response)
              })
              .finally(() => setSubmitting(false))
          } else {
            store.credential.actions
              .update(props.credential?.id, model)
              .then((response) => {
                toast.success("Credential updated")
                props.onSuccess?.(response)
              })
              .finally(() => setSubmitting(false))
          }
        } catch (error) {
          setSubmitting(false)
          throw error
        }
      }}
    >
      <div className="px-9 py-6">
        <div className="flex justify-between items-start gap-8">
          <div className="flex gap-3 items-center flex-1">
            <Link
              to={{
                hash: `settings/credentials`,
              }}
            >
              <PanelButton label="Go back">
                <IconMaterialSymbolsArrowBackRounded />
              </PanelButton>
            </Link>
            <div
              className="bg-card-hover rounded-full p-2.5 mr-1"
              style={{
                backgroundColor: credentialMeta?.color,
              }}
            >
              <img src={credentialMeta?.image_src} className="size-6" />
            </div>
            <div className="leading-6 flex-1">
              <FormFieldEditInline
                after={<p className="text-xs text-secondary">{credentialMeta?.label}</p>}
                name="name"
                form={form}
                onBlur={(value) => {
                  if (!value) {
                    form.setValue("name", "Untitled Credential")
                    toast.info("Credential name cannot be empty")
                  }
                }}
              />
            </div>
          </div>
          <Button type="submit" loading={submitting} className="mr-8">
            Save
          </Button>
        </div>
        <Separator className="mt-5 mb-6" />
        <section className="flex gap-6">
          <aside className="flex flex-col gap-1.5 min-w-[170px]">
            {tabs.map((item) => {
              return (
                <HashSidebarItem
                  {...item}
                  key={item.label}
                  active={item.to?.split("/")?.[0] === props.currentTab}
                  baseHash={`settings/credentials/${props.id}`}
                />
              )
            })}
          </aside>
          <main className="flex-1 -mt-3">
            {props.currentTab === "connection" && (
              <>
                <FormBuilder properties={credentialMeta?.properties} form={form} node_type={props.type} />
                <Button
                  variant={"outline"}
                  type="button"
                  loading={testing}
                  className="mt-5"
                  onClick={() => {
                    setTesting(true)
                    store.credential.actions
                      .testCredentials(form.getValues())
                      .then(() => {
                        toast.success("Connection successful")
                      })
                      .finally(() => {
                        setTesting(false)
                      })
                  }}
                >
                  <IconIconamoonPlayCircle className="size-5" />
                  Test Connection
                </Button>
                {/* <FormField form={form} name="properties.api_key" label="API Key">
                  <Input autoFocus placeholder="Paste your API key here" />
                </FormField> */}
              </>
            )}
            {props.currentTab === "sharing" && (
              <>
                <FormField form={form} name="visibility.type" label="Visibility">
                  <Select>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="private">Private</SelectItem>
                      <SelectItem value="public" disabled>
                        Public
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              </>
            )}
            {props.currentTab === "details" && (
              <>
                <Table>
                  {/* <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Invoice</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader> */}
                  <TableBody>
                    <TableRow className="border-transparent rounded-xl">
                      <TableCell className="font-medium p-3s">Created</TableCell>
                      <TableCell className="text-secondary p-3">{dayjs().to(dayjs(props.credential?.created_at))}</TableCell>
                    </TableRow>
                    <TableRow className="border-transparent">
                      <TableCell className="font-medium p-3s">Last modified</TableCell>
                      <TableCell className="text-secondary p-3">{dayjs().to(dayjs(props.credential?.updated_at))}</TableCell>
                    </TableRow>
                    <TableRow className="border-transparent">
                      <TableCell className="font-medium p-3s">ID</TableCell>
                      <TableCell className="text-secondary p-3">{props.credential?.id}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </>
            )}
          </main>
        </section>
      </div>
    </Form>
  )
}
